import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../constants/providers/AuthProvider';

interface StudentKPIs {
  totalClasses: number;
  classesThisMonth: number;
  streak: number;
}

export function useStudentKPIs() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  const query = useQuery({
    queryKey: ['student-kpis', userId],
    queryFn: async (): Promise<StudentKPIs> => {
      if (!userId) return { totalClasses: 0, classesThisMonth: 0, streak: 0 };

      // Get all attended enrollments
      const { data, error } = await supabase
        .from('class_enrollments')
        .select('attended, checked_in_at, enrolled_at')
        .eq('student_id', userId)
        .eq('attended', true)
        .order('enrolled_at', { ascending: false });

      if (error) throw error;

      const attended = data || [];
      const totalClasses = attended.length;

      // Classes this month
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      const classesThisMonth = attended.filter(
        (e) => e.checked_in_at && new Date(e.checked_in_at) >= monthStart
      ).length;

      // Streak: count consecutive weeks (going back from current week) with at least one check-in
      let streak = 0;
      if (attended.length > 0) {
        const now = new Date();
        const getWeekKey = (d: Date) => {
          const start = new Date(d);
          start.setDate(start.getDate() - start.getDay());
          return `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
        };

        const weekSet = new Set(
          attended
            .filter((e) => e.checked_in_at)
            .map((e) => getWeekKey(new Date(e.checked_in_at!)))
        );

        // Walk back from current week
        const cursor = new Date(now);
        cursor.setDate(cursor.getDate() - cursor.getDay());
        for (let i = 0; i < 52; i++) {
          if (weekSet.has(getWeekKey(cursor))) {
            streak++;
            cursor.setDate(cursor.getDate() - 7);
          } else {
            break;
          }
        }
      }

      return { totalClasses, classesThisMonth, streak };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    totalClasses: query.data?.totalClasses ?? 0,
    classesThisMonth: query.data?.classesThisMonth ?? 0,
    streak: query.data?.streak ?? 0,
    isLoading: query.isLoading,
  };
}
